import { Fetcher } from '@this/src/util';
import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { styled } from '@this/constants/themes';
import moment from 'moment';
import { Modal, ModalBody, ModalHeader } from '@this/components/shared/ui/feedbacks/modal';
import { Button } from '@this/components/shared/ui/inputs/button';
import SimpleLoading from '@this/shared/simple_loading/simple_loading';
import { Table, TableBody, TableCell, TableHead, TableRow } from '@this/components/shared/ui/data_displays/table';
import { Select } from '@this/components/shared/ui/inputs/select';
import { Textarea } from '@this/components/shared/ui/inputs/textarea';
import type {
  JalBookingInventoryCommentArgs,
  CommentType
} from '@this/domain/arrangement/jal_booking_inventory_comment';
import {
  JalBookingInventoryComment,
  commentTypeLabels
} from '@this/domain/arrangement/jal_booking_inventory_comment';
import Notification from '@this/src/notification';

interface Props {
  inventoryPairId: number;
  onClose: () => void;
}

const InventoryCommentModal = observer(({ inventoryPairId, onClose }: Props) => {
  const [isLoading, setIsLoading] = useState(false);
  const [comments, setComments] = useState<JalBookingInventoryComment[]>([]);
  const [content, setContent] = useState('');
  const [status, setStatus] = useState<CommentType>('normal');

  const fetchComments = () => {
    setIsLoading(true);
    Fetcher.get<{ comments: JalBookingInventoryCommentArgs[] }>(
      `/arrangement/auto_reservation_inventory/jal_booking/inventory_pairs/${inventoryPairId}/comments.json`
    )
      .then(res => {
        const instances = res.comments.map(args => new JalBookingInventoryComment(args));
        setComments(instances);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const saveComment = () => {
    const params = {
      content,
      status,
      inventory_pair_id: inventoryPairId
    };
    setIsLoading(true);
    Fetcher.post(
      `/arrangement/auto_reservation_inventory/jal_booking/inventory_pairs/${inventoryPairId}/comments.json`,
      params
    )
      .then(() => {
        setContent('');
        setStatus('normal');
        fetchComments();
      })
      .catch(() => {
        Notification.error('コメントの保存に失敗しました');
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    fetchComments();
  }, [inventoryPairId]);

  return (
    <Modal open onClose={onClose} size="large">
      <ModalHeader>この商品に関するやり取り</ModalHeader>
      <ModalBody>
        {isLoading ? (
          <SimpleLoading />
        ) : (
          <ListSection>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell nowrap>手配者名</TableCell>
                  <TableCell nowrap>種類</TableCell>
                  <TableCell>内容</TableCell>
                  <TableCell nowrap>記載日時</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {comments.map(c => (
                  <TableRow key={c.id}>
                    <TableCell nowrap>{c.commentedByName}</TableCell>
                    <TableCellStatus nowrap status={c.status}>
                      {commentTypeLabels[c.status]}
                    </TableCellStatus>
                    <TableCell>{c.content}</TableCell>
                    <TableCell nowrap>{moment(c.commentedAt).format('YYYY-MM-DD HH:mm')}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ListSection>
        )}

        <Section>
          <Select value={status} onChange={e => setStatus(e.target.value as CommentType)}>
            {Object.entries(commentTypeLabels).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
        </Section>
        <Section>
          <Textarea
            placeholder="コメント内容を入力"
            value={content}
            onChange={e => setContent(e.target.value)}
            rowsMin={5}
            size="medium"
            fullWidth
          />
        </Section>

        <Button onClick={saveComment}>送信</Button>
      </ModalBody>
    </Modal>
  );
});

const Section = styled.div`
  margin: 10px 0;
`;

const ListSection = styled.div`
  max-height: 300px;
  overflow-y: auto;
  margin-bottom: 16px;
`;

const TableCellStatus = styled(TableCell)<{ status?: CommentType }>`
  ${({ status, theme }) => {
    switch (status) {
      case 'important':
        return `
          color: ${theme.tokens.colors.danger.primary};
          font-weight: bold;
        `;
      case 'warning':
        return `
          color: ${theme.tokens.colors.warning.primary};
          font-weight: bold;
        `;
      default:
        return '';
    }
  }}
`;

export default InventoryCommentModal;
